import React from 'react';
import i18next from 'i18next';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash.get';
import getConfig from 'next/config';

import { layout as servicesLayoutPropType } from 'lib/CustomPropTypes/services';
import { children as childrenPropType } from 'lib/CustomPropTypes';
import { TVE_CANONICAL_ROUTE } from 'lib/tve';
import { isTelemundoVertical } from 'lib/vertical';
import { usePrevious } from 'lib/Hooks/usePrevious';
import BTE from 'lib/BTE';

import Ad from 'components/Ad';
import ErrorBoundary from 'components/ErrorBoundary';
import AlertBanner from 'components/AlertBanner';
import Marquee from 'components/Marquee';
import ProgramGuide from 'components/ProgramGuide';

import ServiceHeader from '../Header';
import ServiceFooter from '../Footer';

const AD_RENDER_TIMEOUT = 4 * 1000; // 4 seconds
const AD_STICKY_TIMEOUT = 3 * 1000; // 3 seconds

const mapStateToProps = ({
  article,
  router,
  shared,
  front,
}, { vertical }) => {
  const adsEnabled = get(article, ['content', 0, 'adsEnabled'], true);
  const ecommerceEnabled = get(article, ['content', 0, 'ecommerceEnabled'], false);
  const nativeAd = get(article, 'isNativeAd', false);
  const adsDisabledByCurator = get(front, 'curation.adsDisabled', false);

  return {
    adsEnabled: adsEnabled && !ecommerceEnabled && !nativeAd,
    currentPath: router.path,
    pageView: shared.pageView,
    vertical: shared.vertical || vertical,
    adsDisabledByCurator,
    isEmbedProgramGuide: front.isEmbedProgramGuide,
  };
};

function getShouldShowBannerAd({
  adsDisabledByCurator,
  adsEnabled,
  currentPath,
  pageView,
  vertical,
  forceHideBannerAd,
}) {
  // Blanket disable for video and slideshow pages
  if (forceHideBannerAd || ['video', 'slideshow'].includes(pageView)) {
    return false;
  }
  // Disable on MSNBC TVE page
  if (vertical === 'msnbc' && currentPath.includes(`/${TVE_CANONICAL_ROUTE}`)) {
    return false;
  }

  // [1] If a vertical has no key in `disableAd`, the bannerAd will be enabled for all pageViews
  // [2] An entry of `all` will disable the bannerAd on all pageViews for the given vertical
  // Examples:
  // Disable on Mach fronts and slideshows -- mach: ['front', 'slideshow']
  // Disable on all Mach page types -- mach: ['all']
  const disableAd = {
    leftfield: ['all'],
  };

  const disabledOnPages = disableAd[vertical];

  const isArticleWithAdsEnabled = pageView === 'article' && adsEnabled;
  const isAdsEnabledByCurator = !adsDisabledByCurator;
  const isFrontAndAdsEnabledByCurator = pageView === 'front' && isAdsEnabledByCurator;
  const showBannerAd = isArticleWithAdsEnabled || isFrontAndAdsEnabledByCurator;

  // [1] For verticals without keys in `disableAd`
  if (typeof disabledOnPages === 'undefined' && showBannerAd) {
    return true;
  }

  // [2] For verticals marked `all` or with specific page types
  if (disabledOnPages && (disabledOnPages.includes('all') || disabledOnPages.includes(pageView))) {
    return false;
  }

  return showBannerAd;
}

function HeaderAndFooter(props) {
  const {
    adsEnabled,
    adsDisabledByCurator,
    children,
    currentPath,
    layout,
    pageView,
    vertical,
    isEmbedProgramGuide,
  } = props;

  const { publicRuntimeConfig: { TOP_BANNER_AD_REFRESH_INTERVAL } } = getConfig();

  const refreshIntervalConfigValue = parseInt(TOP_BANNER_AD_REFRESH_INTERVAL, 10);
  const bannerAdRefreshInterval = !Number.isNaN(refreshIntervalConfigValue)
    ? refreshIntervalConfigValue
    // using `undefined` here to allow the ad to fall back to the default refresh
    // interval set globally in `getEnigmaConfig`
    : undefined;

  const [isTopBannerAdUnstuck, setIsTopBannerAdUnstuck] = React.useState(false);
  const [isAdInitialRenderComplete, setIsAdInitialRenderComplete] = React.useState(false);
  const [isScrolledToTop, setIsScrolledToTop] = React.useState(true);

  // using a ref here for `isAdRendered` rather than the state `isAdInitialRenderComplete`
  // declared above, in order to reference it within a timeout set in useEffect below
  const isAdRendered = React.useRef(false);
  const adRenderTimeoutId = React.useRef();
  const adStickyTimeoutId = React.useRef();
  const enigmaAd = React.useRef();

  const previousPageView = usePrevious(pageView);

  // I don't think the below is needed since we no longer "transport" from video/slideshow->fronts
  // but leaving it here for now.
  // When transporting from a video or slideshow to a front, force the banner ad to be hidden
  const forceHideBannerAd = ['video', 'slideshow'].includes(previousPageView) && pageView === 'front';

  React.useEffect(() => {
    // when component first mounts, wait maximum of AD_RENDER_TIMEOUT time,
    // then if the ad render event hasn't fired by then, go ahead and un-stick
    // the banner ad
    adRenderTimeoutId.current = setTimeout(() => {
      if (!isAdRendered.current) {
        // if the ad request hasn't been initiated by now, something is probably
        // wrong. go ahead and unstick so we don't have a permanently `sticky` ad
        setIsTopBannerAdUnstuck(true);
      }
    }, AD_RENDER_TIMEOUT);

    return () => {
      clearTimeout(adRenderTimeoutId.current);
      // clear the timeout for the sticky timeout on unmount as well, just in case
      // we are unmounting before that completes
      clearTimeout(adStickyTimeoutId.current);
    };
  }, []);

  const bannerAdHandler = React.useCallback((handlerEnigmaAd, { EVENTS }) => {
    enigmaAd.current = enigmaAd.current || handlerEnigmaAd;

    enigmaAd.current.on(EVENTS.RENDER, () => {
      isAdRendered.current = true;
      setIsAdInitialRenderComplete(true);
      setIsTopBannerAdUnstuck(false);

      // when the ad first renders, clear the adRenderTimeoutId timeout, and show
      // the ad (keep it sticky) for another AD_STICKY_TIMEOUT amount of time
      clearTimeout(adRenderTimeoutId.current);
      adStickyTimeoutId.current = setTimeout(() => {
        setIsTopBannerAdUnstuck(true);
      }, AD_STICKY_TIMEOUT);
    });
  }, []);

  React.useEffect(() => {
    function handleScroll(scrollDepth) {
      if (scrollDepth === 0) {
        setIsScrolledToTop(true);
      } else {
        setIsScrolledToTop(false);
      }
    }
    BTE.on('scroll', handleScroll, 'throttle');

    return () => {
      BTE.remove('scroll', handleScroll, 'throttle');
    };
  }, [BTE]);

  const bannerAdClasses = classNames(
    'header-and-footer--banner-ad',
    'ad-container topbannerAd',
    {
      'header-and-footer--static': isTopBannerAdUnstuck,
      // The header footer service listens for a class change
      // to recalc its position. The below 👇 classes serve to trigger that
      isAdInitialRenderComplete,
      isScrolledToTop,
    },
  );

  const shouldShowBannerAd = getShouldShowBannerAd({
    adsDisabledByCurator,
    adsEnabled,
    currentPath,
    pageView,
    vertical,
    forceHideBannerAd,
  });

  return (
    <>
      <ErrorBoundary>
        {shouldShowBannerAd && (
          <div className={bannerAdClasses}>
            <Ad
              handler={bannerAdHandler}
              slot="topbanner"
              refreshInterval={bannerAdRefreshInterval}
            />
          </div>
        )}
      </ErrorBoundary>

      <ErrorBoundary>
        {/* Alert for IE11 */}
        <AlertBanner alertText={i18next.t('IE11 ALERT')} />
      </ErrorBoundary>

      <ErrorBoundary>
        <Marquee />
      </ErrorBoundary>

      <ErrorBoundary>
        <ServiceHeader
          adsEnabled={adsEnabled}
          currentPath={currentPath}
          header={layout.header}
          pageView={pageView}
          vertical={vertical}
        />
      </ErrorBoundary>

      {children}

      {isTelemundoVertical(vertical) && !isEmbedProgramGuide && (
        <ErrorBoundary>
          <ProgramGuide url="https://master.d2gnmmimbvzgnk.amplifyapp.com/" />
        </ErrorBoundary>
      )}

      <ErrorBoundary>
        <ServiceFooter footer={layout.footer} />
      </ErrorBoundary>
    </>
  );
}

HeaderAndFooter.propTypes = {
  adsEnabled: PropTypes.bool,
  adsDisabledByCurator: PropTypes.bool,
  children: childrenPropType,
  currentPath: PropTypes.string.isRequired,
  layout: servicesLayoutPropType.isRequired,
  pageView: PropTypes.string,
  vertical: PropTypes.string.isRequired,
  isEmbedProgramGuide: PropTypes.bool,
};

HeaderAndFooter.defaultProps = {
  adsEnabled: true,
  adsDisabledByCurator: false,
  children: null,
  pageView: null,
  isEmbedProgramGuide: false,
};

export { HeaderAndFooter as UnwrappedHeaderAndFooter };

const WrappedHeaderAndFooter = connect(mapStateToProps)(HeaderAndFooter);
export { WrappedHeaderAndFooter as HeaderAndFooter };
